import React, { useState, useEffect } from 'react';
import { ChevronDown, Menu, X, Sun, Moon } from 'lucide-react';
import logo from "../assets/logo.jpeg";
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Logo = ({ isDark }) => (
  <a href="/" className="flex items-center gap-3 select-none">
    <div className="w-14 h-14 flex items-center justify-center">
      <img
        src={logo}
        alt="Kadtech HR Solutions"
        className="w-full h-full object-contain"
      />
    </div>
    <div className="leading-tight">
      <div className="font-extrabold text-[19px] text-[#1f2937] dark:text-foreground tracking-tight">
        Kadtech<span className="text-[#FED547]">.</span>
      </div>
      <div className="text-[10px] text-gray-500 font-medium tracking-wider uppercase">
        HR Solutions
      </div>
    </div>
  </a>
);

const Header = () => {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark') return 'dark';
    return 'light'; 
  });
  
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const [dynamicMenu, setDynamicMenu] = useState([]);

  useEffect(() => {
    const applyThemeClass = (nextTheme) => {
      if (nextTheme === 'dark') document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
    };

    applyThemeClass(theme);
  }, [theme]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await api.get("/nav-filters");
        
        const data = res.data;

        let allSkills = [];
        (data.skills || []).forEach(skillGroup => {
          if (Array.isArray(skillGroup)) {
            allSkills.push(...skillGroup);
          } else if (typeof skillGroup === 'string') {
            try {
              const parsed = JSON.parse(skillGroup);
              if (Array.isArray(parsed)) {
                allSkills.push(...parsed);
              } else {
                allSkills.push(...skillGroup.split(','));
              }
            } catch (e) {
              allSkills.push(...skillGroup.split(','));
            }
          }
        });

        const uniqueSkills = [...new Set(allSkills.map(s => typeof s === 'string' ? s.trim() : s).filter(Boolean))];
        
        const formattedMenu = [
          { label: 'Jobs by Category', key: 'category', items: data.categories || [] },
          { label: 'Jobs by Courses', key: 'course', items: data.course || [] },
          { label: 'Jobs by Cities', key: 'city', items: data.cities || [] },
          { label: 'Jobs by Skills', key: 'skill', items: uniqueSkills },
        ];
        
        setDynamicMenu(formattedMenu);
      } catch (error) {
        console.error("Error fetching filters", error);
      }
    };
    fetchFilters();
  }, []);

  const handleFilterClick = (e, key, value) => {
    e.preventDefault();
    setMobileOpen(false); 
    setOpenMenu(null); 
    navigate(`/job-listing?${key}=${encodeURIComponent(value)}`);
  };

  const handleViewAll = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    navigate('/job-listing'); 
  };

  const handleToggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);
  };

  return (
    <header 
      className="w-full bg-white dark:bg-background shadow-sm sticky top-0 transition-colors duration-200"
      style={{ zIndex: 999999 }}
    >
      <div className="max-w-[1200px] mx-auto px-4 flex items-center justify-between py-3">
        
        <Logo isDark={theme === 'dark'} />

        <nav className="hidden lg:flex items-center justify-center gap-8 flex-1 px-8">
          {dynamicMenu.map((menu, i) => (
            <div
              key={i}
              className="relative"
              onMouseEnter={() => setOpenMenu(i)}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button className="flex items-center gap-1 text-[13.5px] font-medium text-gray-700 dark:text-foreground hover:text-[#FED547] px-3 py-2 transition-colors">
                {menu.label}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === i ? 'rotate-180' : ''}`} />
              </button>
              {openMenu === i && menu.items.length > 0 && (
                <div 
                  className="absolute top-full left-0 min-w-[260px] bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-gray-800 rounded-md py-2 animate-in fade-in slide-in-from-top-1 duration-150 max-h-[400px] overflow-y-auto"
                  style={{ zIndex: 999999 }}
                >
                  {menu.items.map((item, j) => (
                    <button
                      key={j}
                      onClick={(e) => handleFilterClick(e, menu.key, item)}
                      className="block w-full text-left px-4 py-1.5 text-[13px] text-gray-700 dark:text-gray-300 hover:bg-orange-50 hover:text-[#FED547] dark:hover:bg-gray-800 dark:hover:text-[#FED547] transition-colors"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <button 
            onClick={handleViewAll} 
            className="ml-2 px-4 py-2 text-[13.5px] font-semibold text-gray-800 bg-[#FED547] hover:bg-[#e5be3c] rounded transition-colors"
          >
            View all Jobs
          </button>
        </nav>

        <div className="flex items-center gap-2 lg:gap-4">
          <button
            type="button"
            onClick={handleToggleTheme}
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-200 bg-white/90 dark:bg-[#0b0b0b40] dark:border-border text-gray-700 dark:text-foreground hover:border-[#FED547] dark:hover:border-[#FED547] transition-colors"
            aria-label="Toggle dark mode"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-gray-600" />}
          </button>

          <button className="lg:hidden p-2 text-gray-800 dark:text-white" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 max-h-[70vh] overflow-y-auto">
          {dynamicMenu.map((menu, i) => (
            <details key={i} className="border-b border-gray-100 dark:border-gray-800">
              <summary className="px-4 py-3 font-medium text-gray-800 dark:text-foreground cursor-pointer flex items-center justify-between">
                {menu.label}
                <ChevronDown className="w-4 h-4" />
              </summary>
              <div className="pb-2 bg-gray-50 dark:bg-gray-800/50">
                {menu.items.map((it, j) => (
                  <button 
                    key={j} 
                    onClick={(e) => handleFilterClick(e, menu.key, it)} 
                    className="block w-full text-left px-6 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-[#FED547] dark:hover:text-[#FED547]"
                  >
                    {it}
                  </button>
                ))}
              </div>
            </details>
          ))}
          <button 
            onClick={handleViewAll} 
            className="block w-[90%] mx-auto my-4 py-3 text-center font-semibold text-gray-800 bg-[#FED547] rounded"
          >
            View all Jobs
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;